import { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import api, { dataAPI } from '../services/api';
import secureStorage from '../services/secureStorage';
import {
  base32Decode, parseOtpAuthUrl, parseGoogleAuthMigration, parseAnyOtpQr, parseOtpFileText, parseOtpUriText,
  normalizeOtpSecret, otpDedupeKey, splitOtpDuplicates,
} from '../utils/otpParsers';

/**
 * Lógica de datos de los códigos 2FA (TOTP/HOTP), extraída de
 * components/OTPManager.js para que la pantalla nueva (pages/TwoFactor.js)
 * la reutilice sin duplicar el parser ni el guardado. OTPManager.js no se
 * toca: sigue con su propia copia interna de todo esto, funcionando igual
 * que antes.
 *
 * El vault de OTP vive como un ítem normal del vault (`__otp_entries__`,
 * `data.type === 'otp_vault'`), cifrado igual que cualquier otro dato.
 */

// --- TOTP/HOTP ----------------------------------------------------------
// Los parsers (otpauth://, Google Authenticator, ficheros de Aegis/2FAS) viven
// en utils/otpParsers.js, que es código puro y se prueba con Node.
export { parseOtpAuthUrl, parseGoogleAuthMigration, parseAnyOtpQr, parseOtpFileText, parseOtpUriText, normalizeOtpSecret, otpDedupeKey, splitOtpDuplicates };

const HMAC_HASH = { SHA1: 'SHA-1', SHA256: 'SHA-256', SHA512: 'SHA-512' };

const hmac = async (key, message, algorithm) => {
  const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: HMAC_HASH[algorithm] || 'SHA-1' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', cryptoKey, message));
};

export const generateTOTP = async (secret, period = 30, digits = 6, algorithm = 'SHA1') => {
  try {
    const key = base32Decode(secret);
    const time = Math.floor(Date.now() / 1000 / period);
    const timeBuffer = new ArrayBuffer(8);
    new DataView(timeBuffer).setUint32(4, time, false);
    const mac = await hmac(key, new Uint8Array(timeBuffer), algorithm);
    const offset = mac[mac.length - 1] & 0x0f;
    const code = (
      ((mac[offset] & 0x7f) << 24) |
      ((mac[offset + 1] & 0xff) << 16) |
      ((mac[offset + 2] & 0xff) << 8) |
      (mac[offset + 3] & 0xff)
    ) % Math.pow(10, digits);
    return code.toString().padStart(digits, '0');
  } catch (e) {
    console.error('Error generating TOTP:', e);
    return null;
  }
};

/**
 * Decodifica el primer QR de un fichero de imagen (foto o captura). Se usa
 * tanto para "añadir cuenta" como para "importar" — mismo `jsQR` que
 * OTPManager.js, sobre un `<canvas>` fuera de pantalla. Las fotos de más de
 * 2000 px se reducen: jsQR es lineal en píxeles y un QR sigue legible.
 */
const MAX_QR_SIDE = 2000;

/** Decodifica un QR de píxeles RGBA (imagen o fotograma de vídeo); null si no hay. */
export function decodeQrFromImageData(imageData, inversionAttempts = 'attemptBoth') {
  return jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts })?.data || null;
}

export function decodeQrFromImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, MAX_QR_SIDE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(decodeQrFromImageData(ctx.getImageData(0, 0, canvas.width, canvas.height)));
      } catch (e) {
        reject(e);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  });
}

const normalizeOtpEntries = (entries = []) => {
  if (!Array.isArray(entries)) return [];
  return [...entries]
    .map((entry) => ({
      id: entry.id || '',
      issuer: entry.issuer || '',
      account: entry.account || '',
      secret: entry.secret || '',
      type: entry.type || 'totp',
      digits: entry.digits || 6,
      period: entry.period || 30,
      algorithm: entry.algorithm || 'SHA1',
      counter: entry.counter || 0,
      createdAt: entry.createdAt || ''
    }))
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
};

const buildOtpSignature = (entries = []) => JSON.stringify(normalizeOtpEntries(entries));

const OTP_CACHE_VERSION = 'v2';

/**
 * Carga/guarda el vault de OTP (`__otp_entries__`), genera los códigos cada
 * segundo y expone las acciones de alta/baja. Mismo contrato de caché
 * (secureStorage, origin-bound) y de sincronización que OTPManager.js.
 */
export function useOtpEntries() {
  const [entries, setEntries] = useState([]);
  // 'decrypt' cuando el registro de códigos existe pero el servidor no puede
  // descifrarlo: NO es una lista vacía, y la UI debe decirlo.
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);

  const otpDbIdRef = useRef(null);
  const loadedRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const lastSavedSignatureRef = useRef('[]');
  const hasCodesRef = useRef(false);
  // Copia síncrona de `entries` y del error de carga: addEntries se llama varias
  // veces seguidas (cámara) y no puede esperar al siguiente render para deduplicar.
  const entriesRef = useRef([]);
  const loadErrorRef = useRef(null);

  const persistCache = useCallback(async (list, metadata = {}) => {
    if (!secureStorage.isReady()) return;
    await secureStorage.setItem('otp_entries_cache', list);
    await secureStorage.setItem('otp_entries_cache_timestamp', Date.now());
    await secureStorage.setItem('otp_entries_cache_version', OTP_CACHE_VERSION);
    if (metadata.itemId) await secureStorage.setItem('otp_entries_item_id', metadata.itemId);
    if (metadata.itemUpdatedAt) await secureStorage.setItem('otp_entries_item_updated_at', metadata.itemUpdatedAt);
  }, []);

  const clearCache = useCallback(async () => {
    if (!secureStorage.isReady()) return;
    secureStorage.removeItem('otp_entries_cache');
    secureStorage.removeItem('otp_entries_cache_timestamp');
    secureStorage.removeItem('otp_entries_cache_version');
    secureStorage.removeItem('otp_entries_item_id');
    secureStorage.removeItem('otp_entries_item_updated_at');
  }, []);

  const isOtpItem = (item) => item && (item.name === '__otp_entries__' || item.data?.type === 'otp_vault');

  const load = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);

    let cached = null;
    try {
      if (secureStorage.isReady()) {
        const version = await secureStorage.getItem('otp_entries_cache_version');
        if (version && version !== OTP_CACHE_VERSION) {
          await clearCache();
        } else {
          const cachedEntries = await secureStorage.getItem('otp_entries_cache');
          const cachedItemId = await secureStorage.getItem('otp_entries_item_id');
          if (Array.isArray(cachedEntries)) {
            cached = cachedEntries;
            otpDbIdRef.current = cachedItemId || null;
            setEntries(cachedEntries);
            lastSavedSignatureRef.current = buildOtpSignature(cachedEntries);
          }
        }
      }
    } catch (e) {
      console.warn('Error loading OTP cache:', e);
    }

    const rfidId = localStorage.getItem('rfidId');
    if (!rfidId) { setLoading(false); return; }

    try {
      const res = await api.get(`/data/${rfidId}`, { params: { light: '1', search: '__otp_entries__', limit: 10 } });
      const found = (res.data?.data || []).find(isOtpItem);

      if (!found) {
        otpDbIdRef.current = null;
        if (!Array.isArray(cached) || cached.length === 0) {
          setEntries([]);
          lastSavedSignatureRef.current = '[]';
        }
        await clearCache();
        setLoading(false);
        return;
      }

      const detail = await dataAPI.get(found.id || found._id, rfidId);
      const parsed = typeof detail.data?.data === 'string' ? JSON.parse(detail.data.data) : (detail.data?.data || {});
      const list = Array.isArray(parsed.entries) ? parsed.entries : [];

      otpDbIdRef.current = found.id || found._id;
      setEntries(list);
      lastSavedSignatureRef.current = buildOtpSignature(list);
      await persistCache(list, { itemId: otpDbIdRef.current, itemUpdatedAt: detail.data?.updatedAt || null });
    } catch (e) {
      console.error('Error loading OTP entries:', e);
      const msg = String(e?.response?.data?.error || '');
      const kind = e?.response?.status === 500 && /desencript|decrypt/i.test(msg) ? 'decrypt' : 'load';
      loadErrorRef.current = kind;
      setLoadError(kind);
    } finally {
      setLoading(false);
    }
  }, [clearCache, persistCache]);

  useEffect(() => { load(); }, [load]);

  const saveToDB = useCallback(async (list) => {
    const rfidId = localStorage.getItem('rfidId');
    if (!rfidId) return false;

    if (!Array.isArray(list) || list.length === 0) {
      if (otpDbIdRef.current) {
        try {
          await dataAPI.delete(otpDbIdRef.current, rfidId);
        } catch (e) {
          console.error('Error deleting OTP record:', e);
          return false;
        }
        otpDbIdRef.current = null;
      }
      await clearCache();
      return true;
    }

    try {
      const res = await dataAPI.save({
        rfidId,
        name: '__otp_entries__',
        description: 'OTP Authenticator entries (encrypted)',
        data: { type: 'otp_vault', entries: list },
        tags: ['otp', 'system'],
        ...(otpDbIdRef.current ? { itemId: otpDbIdRef.current } : {})
      });

      if (!otpDbIdRef.current) {
        const listRes = await api.get(`/data/${rfidId}`, { params: { light: '1', search: '__otp_entries__', limit: 10 } });
        const found = (listRes.data?.data || []).find(isOtpItem);
        if (found) otpDbIdRef.current = found.id || found._id;
      }

      await persistCache(list, { itemId: otpDbIdRef.current, itemUpdatedAt: res?.data?.updatedAt || null });
      return true;
    } catch (e) {
      console.error('Error saving OTP entries:', e);
      return false;
    }
  }, [clearCache, persistCache]);

  // Guarda con debounce solo cuando el cambio lo hizo el usuario (no la carga inicial)
  useEffect(() => {
    const signature = buildOtpSignature(entries);
    if (isDirtyRef.current && signature !== lastSavedSignatureRef.current) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        if (!isDirtyRef.current) return; // flush() ya lo guardó
        isDirtyRef.current = false;
        if (await saveToDB(entries)) lastSavedSignatureRef.current = buildOtpSignature(entries);
        else isDirtyRef.current = true;
      }, 600);
    }
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [entries, saveToDB]);

  // Códigos TOTP, refrescados cada segundo. Ver el comentario largo en
  // OTPManager.js sobre por qué `codes` no entra en las deps del timer.
  const generateAllCodes = useCallback(async () => {
    const newCodes = {};
    for (const entry of entries) {
      if (entry.type === 'totp') {
        const code = await generateTOTP(entry.secret, entry.period, entry.digits, entry.algorithm);
        if (code) newCodes[entry.id] = code;
      }
    }
    hasCodesRef.current = Object.keys(newCodes).length > 0;
    setCodes((prev) => (Object.keys(prev).length === 0 && !hasCodesRef.current ? prev : newCodes));
  }, [entries]);

  // Una cuenta recién añadida (o cargada) necesita su código ya, no en el siguiente
  // cambio de ventana de 30 s. `codes` no entra en las deps: ver OTPManager.js.
  useEffect(() => { generateAllCodes(); }, [generateAllCodes]);

  useEffect(() => {
    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = 30 - (now % 30);
      setTimeLeft(remaining);
      if (remaining === 30 || !hasCodesRef.current) generateAllCodes();
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [generateAllCodes]);

  // Mantiene la copia síncrona alineada con el estado (carga, borrado).
  useEffect(() => { entriesRef.current = entries; }, [entries]);

  /**
   * Añade un lote con UNA sola escritura (la del debounce, o antes si se llama a
   * flush()). Descarta duplicados por secreto, frente a lo ya guardado y dentro
   * del propio lote. Devuelve { added, skipped, blocked }: blocked = el registro
   * de 2FA no se pudo leer; escribir encima lo pisaría, así que no se añade nada.
   */
  const addEntries = useCallback((parsedList) => {
    if (loadErrorRef.current) return { added: 0, skipped: 0, blocked: loadErrorRef.current };
    const { fresh, duplicates } = splitOtpDuplicates(parsedList, entriesRef.current);
    if (fresh.length === 0) return { added: 0, skipped: duplicates.length, blocked: null };
    const now = new Date().toISOString();
    const withIds = fresh.map((entry, i) => ({
      id: `${Date.now()}${i}${Math.random().toString(36).slice(2, 9)}`,
      ...entry,
      createdAt: now
    }));
    const next = [...entriesRef.current, ...withIds];
    entriesRef.current = next;
    isDirtyRef.current = true;
    setEntries(next);
    return { added: fresh.length, skipped: duplicates.length, blocked: null };
  }, []);

  const addEntry = useCallback((entry) => addEntries([entry]), [addEntries]);

  /** Guarda ya lo pendiente (sin esperar al debounce). true si no queda nada sin guardar. */
  const flush = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (!isDirtyRef.current) return true;
    isDirtyRef.current = false;
    const list = entriesRef.current;
    const ok = await saveToDB(list);
    if (ok) lastSavedSignatureRef.current = buildOtpSignature(list);
    else isDirtyRef.current = true;
    return ok;
  }, [saveToDB]);

  // Al desmontar (cerrar el modal o cambiar de pantalla dentro de los 600 ms del
  // debounce) el cleanup del efecto de arriba cancela el temporizador: sin esto
  // lo último que se añadió se perdería.
  const flushRef = useRef(flush);
  useEffect(() => { flushRef.current = flush; }, [flush]);
  useEffect(() => () => { flushRef.current(); }, []);

  const deleteEntry = useCallback((id) => {
    if (loadErrorRef.current) return;
    const next = entriesRef.current.filter((e) => e.id !== id);
    entriesRef.current = next;
    isDirtyRef.current = true;
    setEntries(next);
  }, []);

  return { entries, loading, loadError, codes, timeLeft, addEntry, addEntries, flush, deleteEntry };
}

export default useOtpEntries;
